/**
 * Choosing a library field to attach.
 *
 * Filtering happens in the browser over the whole library, because someone
 * building a product types, reads, retypes - and a round trip per keystroke
 * makes that feel like wading. It matches on wording, on key and on entity,
 * since an author arrives from whichever of the three they happen to remember.
 */
import { useMemo, useState } from "react";
import type { DataType } from "@dp/shared";
import { DATA_TYPE_LABELS, type FieldRow } from "../lib/types.ts";

const SHOWN = 40;

export function FieldPicker({
  fields, entityKey, alreadyUsed, onPick, onCancel,
}: {
  fields: FieldRow[];
  /** Only fields of this entity can go in this section. */
  entityKey: string;
  alreadyUsed: Set<string>;
  onPick: (field: FieldRow) => void;
  onCancel: () => void;
}) {
  const [term, setTerm] = useState("");

  const candidates = useMemo(() => {
    const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return fields
      .filter((field) => field.entityKey === entityKey && field.active)
      .filter((field) => {
        if (!words.length) return true;
        const haystack = `${field.label} ${field.key} ${field.entityLabel} ${field.helpText ?? ""}`.toLowerCase();
        return words.every((word) => haystack.includes(word));
      });
  }, [fields, entityKey, term]);

  const shown = candidates.slice(0, SHOWN);

  return (
    <div className="picker">
      <div className="picker-head">
        <input
          className="control" type="search" autoFocus placeholder="Search the library…"
          value={term} onChange={(event) => setTerm(event.target.value)}
        />
        <button type="button" className="btn btn-sm" onClick={onCancel}>Close</button>
      </div>

      {candidates.length === 0 ? (
        <div className="picker-empty small muted">
          {fields.some((field) => field.entityKey === entityKey)
            ? "Nothing in the library matches that."
            : "The library has no fields for this entity yet. Define one first."}
        </div>
      ) : (
        <div className="picker-list">
          {shown.map((field) => {
            const used = alreadyUsed.has(field.key);
            return (
              <button
                key={field.key} type="button" className="picker-row" disabled={used}
                onClick={() => onPick(field)}
              >
                <span className="picker-label">
                  {field.label}
                  {used && <span className="chip" style={{ marginLeft: 7 }}>already asked</span>}
                </span>
                <span className="picker-meta">
                  <span className="chip">{DATA_TYPE_LABELS[field.dataType as DataType]}</span>
                  <span className="key">{field.key}</span>
                </span>
              </button>
            );
          })}
          {candidates.length > SHOWN && (
            <div className="picker-more small muted">{candidates.length - SHOWN} more match — keep typing.</div>
          )}
        </div>
      )}
    </div>
  );
}
